import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import type { Team, TeamDetails, TeamMember, Document } from "@/lib/api";

// --- Query hooks ---

export function useTeams() {
  const { fetchWithAuth } = useApiContext();

  return useQuery({
    queryKey: ["teams"],
    queryFn: async () => {
      const response = await fetchWithAuth(`${API_BASE}/teams`);
      return handleResponse<Team[]>(response);
    },
  });
}

export function useTeam(id: string) {
  const { fetchWithAuth } = useApiContext();

  return useQuery({
    queryKey: ["team", id],
    queryFn: async () => {
      const response = await fetchWithAuth(`${API_BASE}/teams/${id}`);
      return handleResponse<TeamDetails>(response);
    },
    enabled: !!id,
  });
}

export function useTeamMembers(teamId: string) {
  const { fetchWithAuth } = useApiContext();

  return useQuery({
    queryKey: ["team-members", teamId],
    queryFn: async () => {
      const response = await fetchWithAuth(`${API_BASE}/teams/${teamId}/members`);
      return handleResponse<TeamMember[]>(response);
    },
    enabled: !!teamId,
  });
}

export function useTeamDocuments(teamId: string) {
  const { fetchWithAuth } = useApiContext();

  return useQuery({
    queryKey: ["team-documents", teamId],
    queryFn: async () => {
      const response = await fetchWithAuth(`${API_BASE}/teams/${teamId}/documents`);
      return handleResponse<Document[]>(response);
    },
    enabled: !!teamId,
  });
}

// --- Mutation hooks ---

export function useCreateTeam() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (data: { name: string; slug?: string; image?: string }) => {
      const response = await fetchWithAuth(`${API_BASE}/teams`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return handleResponse<Team>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; slug?: string; image?: string } }) => {
      const response = await fetchWithAuth(`${API_BASE}/teams/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return handleResponse<Team>(response);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["team", variables.id] });
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetchWithAuth(`${API_BASE}/teams/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete team");
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}

export function useInviteMember(teamId: string) {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (data: { email: string; role: string }) => {
      const response = await fetchWithAuth(`${API_BASE}/teams/${teamId}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return handleResponse<TeamMember>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["team", teamId] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}

export function useUpdateMemberRole(teamId: string) {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const response = await fetchWithAuth(`${API_BASE}/teams/${teamId}/members/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      return handleResponse<TeamMember>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["team", teamId] });
    },
  });
}

export function useRemoveMember(teamId: string) {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await fetchWithAuth(`${API_BASE}/teams/${teamId}/members/${userId}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to remove team member");
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["team", teamId] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}
